/**
 * Normalise a location filter value into a list of concrete location IDs.
 *
 * The filter contract stores `['all']` when every location is selected and an
 * explicit empty array when None is selected. This helper keeps those two
 * states distinct:
 * - `['all']` -> `['all']`
 * - `[]` -> `['0']` (an impossible id used to request no rows)
 * - `['1', '2']` -> `['1', '2']`
 * - null/undefined/empty string -> `[]` (legacy "no restriction")
 */
export const getSelectedLocationIds = (value: unknown): string[] => {
  if (value === null || value === undefined || value === '') return [];

  if (Array.isArray(value) && value.length === 0) {
    return ['0'];
  }

  const values = Array.isArray(value) ? value : `${value}`.split(',');
  const ids = Array.from(new Set(values.map((item) => `${item}`.trim()).filter((item) => item.length > 0)));

  if (ids.length === 1 && ids[0] === 'all') {
    return ['all'];
  }

  return ids.filter((id) => id !== 'all');
};

/**
 * Serialise one or more location IDs for the reporting API. The API accepts a
 * comma-separated `locationId` value and keeps backwards compatibility with a
 * single ID. An empty legacy value means All; explicit None is `['0']`.
 */
export const getLocationQueryValue = (value: unknown): string => {
  const ids = getSelectedLocationIds(value);
  return ids.length ? ids.join(',') : 'all';
};
